/**
 * MathFormula.tsx
 * Enhanced KaTeX formula rendering component with responsive handling
 */

import React from 'react';
import 'katex/dist/katex.min.css';
import './math-formulas.css';
import { InlineMath, BlockMath } from 'react-katex';
import { cn } from '@vsat/shared/lib/utils';

interface Props {
  children: string;
  block?: boolean;
  className?: string;
}

export default function MathFormula({ children, block = false, className }: Props) {
  try {
    // Add \displaystyle to inline math for better vertical alignment
    // Only add if not already present to avoid duplication
    let mathContent = children.trim();
    if (!block && !mathContent.startsWith('\\displaystyle')) {
      mathContent = `\\displaystyle ${mathContent}`;
    }

    const mathComponent = block ? (
      <BlockMath math={mathContent} />
    ) : (
      <InlineMath math={mathContent} />
    );

    return (
      <span
        className={cn(
          'math-formula',
          block && 'block my-2 overflow-x-auto',
          !block && 'inline-block',
          className
        )}
        style={!block ? { verticalAlign: 'baseline', lineHeight: 'inherit' } : undefined}
      >
        {mathComponent}
      </span>
    );
  } catch (error) {
    return (
      <span className='text-red-500 text-sm bg-red-50 px-2 py-1 rounded border'>
        Invalid formula: {children}
      </span>
    );
  }
}

// Parse text with formulas - enhanced for better handling
export function parseFormulas(text: string): React.ReactNode[] {
  // Add null/undefined check
  if (!text || typeof text !== 'string') {
    return [<span key='empty'></span>];
  }

  const parts: React.ReactNode[] = [];
  let key = 0;

  // Enhanced regex patterns for better formula detection
  const displayMathRegex = /\$\$([\s\S]*?)\$\$/g;
  const inlineMathRegex = /\$([^$\n]+?)\$/g;

  // First pass: handle display math $$...$$
  let textAfterDisplay = text.replace(displayMathRegex, (match, formula) => {
    return `__DISPLAY_MATH_${key++}_${formula}__`;
  });

  // Second pass: handle inline math $...$
  // Use non-capturing groups to avoid duplication in split results
  const finalParts = textAfterDisplay.split(/(__DISPLAY_MATH_\d+_.*?__|\$[^$\n]+?\$)/g);

  finalParts.forEach(part => {
    if (!part) return; // Skip empty/undefined parts

    if (part.startsWith('__DISPLAY_MATH_')) {
      const matches = part.match(/__DISPLAY_MATH_\d+_(.*?)__/);
      if (matches) {
        const formula = matches[1];
        parts.push(
          <MathFormula key={`display-${key++}`} block className='text-center'>
            {formula}
          </MathFormula>
        );
      }
    } else if (part.startsWith('$') && part.endsWith('$')) {
      const formula = part.slice(1, -1);
      parts.push(
        <MathFormula key={`inline-${key++}`} className='mx-1'>
          {formula}
        </MathFormula>
      );
    } else {
      // Handle line breaks and preserve formatting
      const textParts = part.split('\n');
      textParts.forEach((textPart, index) => {
        if (textPart) {
          parts.push(<span key={`text-${key++}`}>{textPart}</span>);
        }
        if (index < textParts.length - 1) {
          parts.push(<br key={`br-${key++}`} />);
        }
      });
    }
  });

  return parts;
}

// Enhanced formula parsing with better error handling
export function parseFormulasWithFallback(text: string, fallback?: string): React.ReactNode[] {
  try {
    return parseFormulas(text);
  } catch (error) {
    console.warn('Formula parsing error:', error);
    return fallback ? parseFormulas(fallback) : [<span key='error'>{text}</span>];
  }
}
